/**
 * useBolSequence.js
 * ─────────────────
 * React hook managing per-project BOL number sequence counters
 * stored in localStorage.
 *
 * localStorage key pattern: tmg_bol_seq_[projectId]
 * Value: integer (last used sequence number, starts at 0)
 */

import { useCallback } from 'react';

const LS_PREFIX = 'tmg_bol_seq_';

function getKey(projectId) {
  return `${LS_PREFIX}${projectId}`;
}

function readSeq(projectId) {
  try {
    const val = localStorage.getItem(getKey(projectId));
    if (val !== null) {
      const n = parseInt(val, 10);
      return isNaN(n) ? 0 : n;
    }
  } catch { /* ignore */ }
  return 0;
}

function writeSeq(projectId, seq) {
  try { localStorage.setItem(getKey(projectId), String(seq)); } catch { /* ignore */ }
}

function padSeq(n) {
  return String(n).padStart(3, '0');
}

export function useBolSequence() {
  /**
   * Get the next BOL number and increment the counter.
   * Returns: 'BOL-{projectNumber}-{paddedSeq}'
   * Example: 'BOL-TEMA2041-001'
   */
  const getNextBolNumber = useCallback((projectId, projectNumber) => {
    const current = readSeq(projectId);
    const next = current + 1;
    writeSeq(projectId, next);
    return `BOL-${projectNumber}-${padSeq(next)}`;
  }, []);

  /**
   * Get the next NBR number using the same shared sequence.
   * Returns: 'NBR-{projectNumber}-{paddedSeq}'
   * Example: 'NBR-TEMA2041-003'
   */
  const getNextNbrNumber = useCallback((projectId, projectNumber) => {
    const current = readSeq(projectId);
    const next = current + 1;
    writeSeq(projectId, next);
    return `NBR-${projectNumber}-${padSeq(next)}`;
  }, []);

  /**
   * Peek at the current sequence number without incrementing.
   * Returns the last used number (0 if never used).
   */
  const peekCurrentSequence = useCallback((projectId) => {
    return readSeq(projectId);
  }, []);

  /**
   * Reset the sequence to 0 for a project. Testing only — not exposed in UI.
   */
  const resetSequence = useCallback((projectId) => {
    writeSeq(projectId, 0);
  }, []);

  return {
    getNextBolNumber,
    getNextNbrNumber,
    peekCurrentSequence,
    resetSequence,
  };
}
